import type { SVGProps } from "react";

/**
 * Conjunto minimo de iconos en SVG en linea. La app no carga una fuente
 * de iconos (Material Symbols u otra), asi que los pocos glifos que usan
 * los dialogos About/Feedback se definen aqui como trazos vectoriales.
 * El estilo (trazo de 1.6, extremos redondeados) sigue el del isotipo
 * AxonMark para mantener coherencia visual.
 *
 * El color se hereda via `currentColor`, de modo que cualquier clase de
 * texto (`text-[var(--color-brand)]`, etc.) tine el icono.
 */
export type IconName =
  | "info"
  | "comment"
  | "send"
  | "open_in_new"
  | "check"
  | "check_circle"
  | "close"
  | "face_sad"
  | "face_neutral"
  | "face_happy"
  | "light_mode"
  | "dark_mode"
  | "school"
  | "arrow_forward"
  | "arrow_right"
  | "collapse"
  | "event"
  | "laptop"
  | "premium";

interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 16, className, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}

const PATHS: Record<IconName, JSX.Element> = {
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </>
  ),
  comment: (
    <>
      <path d="M4 5h16v11H9l-4 3v-3H4z" />
      <path d="M8 9.5h8" />
      <path d="M8 12.5h5" />
    </>
  ),
  send: (
    <>
      <path d="M5 12l15-7-7 15-2-6z" />
      <path d="M11 14l3-3" />
    </>
  ),
  open_in_new: (
    <>
      <path d="M14 5h5v5" />
      <path d="M19 5l-8 8" />
      <path d="M18 13v6H5V6h6" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7" />,
  check_circle: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l2.5 2.5L16 9.5" />
    </>
  ),
  close: (
    <>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </>
  ),
  face_sad: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 15.5c1-1.2 2.1-1.8 3.5-1.8s2.5.6 3.5 1.8" />
      <path d="M9 9.5h.01" />
      <path d="M15 9.5h.01" />
    </>
  ),
  face_neutral: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 15h7" />
      <path d="M9 9.5h.01" />
      <path d="M15 9.5h.01" />
    </>
  ),
  face_happy: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 14c1 1.2 2.1 1.8 3.5 1.8s2.5-.6 3.5-1.8" />
      <path d="M9 9.5h.01" />
      <path d="M15 9.5h.01" />
    </>
  ),
  light_mode: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v2" />
      <path d="M12 19v2" />
      <path d="M5 5l1.4 1.4" />
      <path d="M17.6 17.6L19 19" />
      <path d="M3 12h2" />
      <path d="M19 12h2" />
      <path d="M5 19l1.4-1.4" />
      <path d="M17.6 6.4L19 5" />
    </>
  ),
  dark_mode: <path d="M20 14.5A8 8 0 0 1 9.5 4 6.5 6.5 0 1 0 20 14.5z" />,
  school: (
    <>
      <path d="M12 5 3 9l9 4 9-4-9-4z" />
      <path d="M7 11v4c0 1.1 2.2 2 5 2s5-.9 5-2v-4" />
      <path d="M21 9v4" />
    </>
  ),
  arrow_forward: (
    <>
      <path d="M5 12h13" />
      <path d="M12 6l6 6-6 6" />
    </>
  ),
  arrow_right: <path d="M9 6l6 6-6 6" />,
  collapse: (
    <>
      <path d="M8 8l4 4 4-4" />
      <path d="M8 16l4-4 4 4" />
    </>
  ),
  event: (
    <>
      <rect x="4" y="6" width="16" height="14" rx="1.5" />
      <path d="M4 10h16" />
      <path d="M8 4v3" />
      <path d="M16 4v3" />
    </>
  ),
  laptop: (
    <>
      <rect x="5" y="5" width="14" height="10" rx="1" />
      <path d="M3 19h18" />
    </>
  ),
  premium: (
    <>
      <circle cx="12" cy="9" r="5" />
      <path d="M9 13l-1.5 7 4.5-2.5 4.5 2.5-1.5-7" />
    </>
  ),
};
